/**
 * Phase 8 #7 — Evaluator for the richer visibility predicate stored
 * on `MetadataSchemaField.visibleWhenJson`. Supersedes the legacy
 * single-field equality pair (dependsOnFieldKey + dependsOnValue) when
 * set; falls back to the legacy pair otherwise.
 *
 * Predicate shape:
 *   {
 *     "all": [                                   // optional AND-list
 *       { "fieldKey": "kind", "op": "eq", "value": "DAMAGED" },
 *       { "fieldKey": "severity", "op": "in", "value": ["HIGH", "CRITICAL"] }
 *     ],
 *     "any": [...]                               // optional OR-list
 *   }
 *
 * Operators: eq, neq, in, notIn, gt, gte, lt, lte, isNull, isNotNull.
 *
 * The evaluator is permissive on errors: malformed JSON, unknown ops,
 * or missing fields default the predicate to "true" so a broken
 * schema doesn't strand the operator with an empty form.
 */

export type VisibilityOp =
  | 'eq' | 'neq'
  | 'in' | 'notIn'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'isNull' | 'isNotNull';

export interface VisibilityClause {
  fieldKey: string;
  op: VisibilityOp;
  value?: unknown;
}

export interface VisibilityPredicate {
  all?: VisibilityClause[];
  any?: VisibilityClause[];
}

/**
 * Evaluate a parsed predicate against the current form values.
 * - AND-combined `all` clauses: every entry must match.
 * - OR-combined `any` clauses: at least one entry must match.
 * - When both are present, both must hold (AND of the two groups).
 * - Empty predicate (no `all`, no `any`) -> always visible.
 */
export function evaluateVisibility(
  predicate: VisibilityPredicate,
  formValues: Record<string, unknown>
): boolean {
  const allOk = !predicate.all || predicate.all.length === 0
    ? true
    : predicate.all.every((c) => evaluateClause(c, formValues));
  const anyOk = !predicate.any || predicate.any.length === 0
    ? true
    : predicate.any.some((c) => evaluateClause(c, formValues));
  return allOk && anyOk;
}

/** Parse `visibleWhenJson` defensively. Returns null on any parse error. */
export function parseVisibility(json: string | null | undefined): VisibilityPredicate | null {
  if (!json || !json.trim()) return null;
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!parsed || typeof parsed !== 'object') return null;
    const obj = parsed as { all?: unknown; any?: unknown };
    const out: VisibilityPredicate = {};
    if (Array.isArray(obj.all)) out.all = obj.all.filter(isValidClause);
    if (Array.isArray(obj.any)) out.any = obj.any.filter(isValidClause);
    // Reject if both ended up empty — that's a malformed predicate.
    if ((!out.all || out.all.length === 0) && (!out.any || out.any.length === 0)) return null;
    return out;
  } catch {
    return null;
  }
}

function isValidClause(c: unknown): c is VisibilityClause {
  if (!c || typeof c !== 'object') return false;
  const obj = c as Record<string, unknown>;
  return typeof obj.fieldKey === 'string' && typeof obj.op === 'string';
}

function evaluateClause(clause: VisibilityClause, formValues: Record<string, unknown>): boolean {
  const actual = formValues[clause.fieldKey];
  switch (clause.op) {
    case 'isNull':
      return actual === null || actual === undefined || actual === '';
    case 'isNotNull':
      return actual !== null && actual !== undefined && actual !== '';
    case 'eq':
      return looseEq(actual, clause.value);
    case 'neq':
      return !looseEq(actual, clause.value);
    case 'in':
      return Array.isArray(clause.value) && clause.value.some((v) => looseEq(actual, v));
    case 'notIn':
      return Array.isArray(clause.value) && !clause.value.some((v) => looseEq(actual, v));
    case 'gt':
    case 'gte':
    case 'lt':
    case 'lte':
      return compare(actual, clause.value, clause.op);
    default:
      return true; // unknown op -> permissive (don't strand the form)
  }
}

function looseEq(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null || b == null) return false;
  // Booleans authored as "true"/"false" strings in JSON should match
  // boolean form values.
  if (typeof a === 'boolean') return String(a) === String(b);
  if (typeof b === 'boolean') return String(b) === String(a);
  // Number-vs-string coercion (radio button values arrive as strings).
  if (typeof a === 'number' || typeof b === 'number') {
    const na = Number(a), nb = Number(b);
    if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  }
  return String(a) === String(b);
}

function compare(a: unknown, b: unknown, op: 'gt' | 'gte' | 'lt' | 'lte'): boolean {
  const na = Number(a);
  const nb = Number(b);
  if (Number.isNaN(na) || Number.isNaN(nb)) return false;
  switch (op) {
    case 'gt': return na > nb;
    case 'gte': return na >= nb;
    case 'lt': return na < nb;
    case 'lte': return na <= nb;
  }
}
